/**
 * A call reaches a Contact Center VOICE INBOX only when the bot says so: before
 * the transfer it must send `send_user_details` with
 * `extra_data.handoff_type = "voice_inbox"` (BotToPlatform). Without it the
 * engine falls back to the channel's phone/SIP routing rule
 * (`destinationSource: channel_config`) and rings that number instead.
 *
 * The Supervisor routes escalations to node 7900 by number, so 7900 stays the
 * entry point and becomes three steps:
 *   7900  SysAssignVariable — the handoff variables
 *   N1    BotToPlatform     — send_user_details carrying them in extra_data
 *   N2    the original 7900 — its message + xfer_to_agent
 *
 * Every OTHER hand-off node (the Supervisor's frustration route, 7910, is one)
 * would transfer without those variables and ring the routing rule's number,
 * so it is rewired: it keeps its message and continues into 7900 instead of
 * transferring itself.
 */

export const ESCALATION_ENTRY_NODE = 7900;

export const HANDOFF_VARIABLES = {
  EMPTY: '',
  HANDOFF_TYPE: 'voice_inbox',
  CALL_MOTIVE: 'general',
  FILLER_PHRASE: 'Connecting you with a specialist now.',
  HANDOFF_FAILOVER_MSG: 'Sorry, no one is available right now.',
} as const;

const VARIABLE_NAMES = Object.keys(HANDOFF_VARIABLES);

/** The slice of a table row this plan reads. */
export interface EscalationRow {
  id: string;
  nodeNumber: number | string | null;
  nodeType?: string | null;
  command?: string | null;
  message?: string | null;
  behaviors?: string[] | null;
  parameterInput?: unknown;
  whatsNext?: string[] | null;
}

export interface EscalationRowConfig {
  nodeNumber: number;
  nodeType: 'a' | 'd';
  nodeName: string;
  command?: string;
  message?: string;
  behaviors?: string[];
  parameterInput?: Record<string, unknown>;
  output?: string[];
  decisionVariable?: string;
  whatsNext?: string[];
  variables?: string[];
  answerRequired?: boolean;
}

/** A hand-off node to continue into the entry instead of transferring itself. */
export const REWIRE_TO_ENTRY = {
  behaviors: [] as string[],
  nextNodes: [ESCALATION_ENTRY_NODE],
};

export type VoiceInboxEscalationPlan =
  | { kind: 'present'; rewire: string[] }
  | { kind: 'no-entry' }
  | {
      kind: 'add';
      /** Row ids of other hand-off nodes, to get `REWIRE_TO_ENTRY`. */
      rewire: string[];
      entryRowId: string;
      /** Replaces the entry row (7900) in place. */
      entry: Omit<EscalationRowConfig, 'nodeNumber'>;
      /** Inserted below the entry row, in this order. */
      send: EscalationRowConfig;
      transfer: EscalationRowConfig;
    };

const asObject = (v: unknown): Record<string, unknown> | null => {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  if (typeof v === 'string') {
    try {
      return asObject(JSON.parse(v));
    } catch {
      return null;
    }
  }
  return null;
};

const sendsVoiceInbox = (row: EscalationRow) => {
  if (row.command !== 'BotToPlatform') return false;
  const p = asObject(row.parameterInput);
  const extra = asObject(asObject(p?.global)?.extra_data);
  return p?.schema === 'send_user_details' && extra?.handoff_type != null;
};

const transfersItself = (r: EscalationRow) =>
  r.nodeType === 'd' && (r.behaviors ?? []).includes('xfer_to_agent');

export const planVoiceInboxEscalation = (
  rows: EscalationRow[]
): VoiceInboxEscalationPlan => {
  const send = rows.find(sendsVoiceInbox);
  if (send) {
    // The transfer the send step leads to is the one hand-off that stays.
    const kept = new Set(
      (send.whatsNext ?? []).map((w) => Number(String(w).split('~')[1]))
    );
    return {
      kind: 'present',
      rewire: rows
        .filter((r) => transfersItself(r) && !kept.has(Number(r.nodeNumber)))
        .map((r) => r.id),
    };
  }

  const entry = rows.find(
    (r) =>
      Number(r.nodeNumber) === ESCALATION_ENTRY_NODE &&
      r.nodeType === 'd' &&
      (r.behaviors ?? []).includes('xfer_to_agent')
  );
  if (!entry) return { kind: 'no-entry' };

  const used = new Set(rows.map((r) => Number(r.nodeNumber)));
  const free = (from: number) => {
    let n = from;
    while (used.has(n)) n += 1;
    used.add(n);
    return n;
  };
  const sendNode = free(ESCALATION_ENTRY_NODE + 1);
  const transferNode = free(sendNode + 1);

  return {
    kind: 'add',
    rewire: rows
      .filter((r) => r.id !== entry.id && transfersItself(r))
      .map((r) => r.id),
    entryRowId: entry.id,
    entry: {
      nodeType: 'a',
      nodeName:
        'Supervisor Agent determines that we need to go to escalation (set handoff variables)',
      command: 'SysAssignVariable',
      message: '',
      behaviors: [],
      parameterInput: { set: { ...HANDOFF_VARIABLES } },
      output: ['success'],
      decisionVariable: 'success',
      whatsNext: [`true~${sendNode}`, `false~${transferNode}`],
      variables: VARIABLE_NAMES,
    },
    send: {
      nodeNumber: sendNode,
      nodeType: 'a',
      nodeName: 'Escalation: send voice inbox handoff details (BotToPlatform)',
      command: 'BotToPlatform',
      parameterInput: {
        schema: 'send_user_details',
        global: {
          first_name: 'EMPTY',
          last_name: 'EMPTY',
          email_address: 'EMPTY',
          phone_number: 'EMPTY',
          extra_data: {
            handoff_type: 'HANDOFF_TYPE',
            call_motive: 'CALL_MOTIVE',
            filler_phrase: 'FILLER_PHRASE',
            handoff_failover_message: 'HANDOFF_FAILOVER_MSG',
          },
        },
      },
      output: ['success'],
      decisionVariable: 'success',
      whatsNext: [`true~${transferNode}`, `false~${transferNode}`],
      variables: VARIABLE_NAMES,
    },
    transfer: {
      nodeNumber: transferNode,
      nodeType: 'd',
      nodeName: 'Escalation: transfer to a live agent',
      message: entry.message ?? '',
      behaviors: ['xfer_to_agent'],
      answerRequired: false,
    },
  };
};
